import "./index.css";
// src/DebugPanel/DebugPanelContainer.tsx
import React5 from "react";

// src/DebugPanel/DebugPanel.tsx
import React4, { useState as useState4 } from "react";
import { Rnd } from "react-rnd";
import Tabs from "@cloudscape-design/components/tabs";
import { useAppTranslation as useAppTranslation4 } from "@amzn/awsmp-max-translations";

// src/DebugPanel/StateTab.tsx
import React2, { useState as useState2, useMemo } from "react";
import Box from "@cloudscape-design/components/box";
import SpaceBetween2 from "@cloudscape-design/components/space-between";
import { ObjectInspector } from "react-inspector";
import { useAppTranslation as useAppTranslation2 } from "@amzn/awsmp-max-translations";

// src/DebugPanel/SearchBar.tsx
import { useState, useRef, useEffect } from "react";
import Button from "@cloudscape-design/components/button";
import Autosuggest from "@cloudscape-design/components/autosuggest";
import TokenGroup from "@cloudscape-design/components/token-group";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { useAppTranslation } from "@amzn/awsmp-max-translations";
import { jsx, jsxs } from "react/jsx-runtime";
var SearchBar = ({
  searchTokens,
  onTokensChange,
  availableKeys = [],
  placeholder = "Search...",
  recording,
  onToggleRecording,
  onExport,
  onClear,
  tail,
  onToggleTail,
  expandAll,
  onToggleExpandAll
}) => {
  const { t } = useAppTranslation();
  const [inputValue, setInputValue] = useState("");
  const wrapperRef = useRef(null);
  const suggestions = inputValue.trim() ? availableKeys.filter((key) => key.toLowerCase().includes(inputValue.toLowerCase())).map((key) => ({ value: key })) : [];
  const addToken = (value) => {
    const trimmed = value.trim();
    if (trimmed && !searchTokens.includes(trimmed)) {
      onTokensChange([...searchTokens, trimmed]);
    }
    setInputValue("");
  };
  const removeToken = (index) => {
    onTokensChange(searchTokens.filter((_, i) => i !== index));
  };
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper)
      return;
    const handleKeyDown = (event) => {
      if (event.key === " " || event.key === ",") {
        event.preventDefault();
        const trimmed = inputValue.trim();
        if (trimmed && !searchTokens.includes(trimmed)) {
          onTokensChange([...searchTokens, trimmed]);
          setInputValue("");
        }
      } else if (event.key === "Enter" && suggestions.length === 0) {
        event.preventDefault();
        const trimmed = inputValue.trim();
        if (trimmed && !searchTokens.includes(trimmed)) {
          onTokensChange([...searchTokens, trimmed]);
          setInputValue("");
        }
      } else if (event.key === "Backspace" && inputValue === "" && searchTokens.length > 0) {
        event.preventDefault();
        onTokensChange(searchTokens.filter((_, i) => i !== searchTokens.length - 1));
      }
    };
    const inputElement = wrapper.querySelector("input");
    if (inputElement) {
      inputElement.addEventListener("keydown", handleKeyDown);
      return () => inputElement.removeEventListener("keydown", handleKeyDown);
    }
  }, [inputValue, searchTokens, onTokensChange, suggestions.length]);
  return /* @__PURE__ */ jsxs(SpaceBetween, { size: "xs", direction: "vertical", children: [
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: "8px", alignItems: "center" }, children: [
      onToggleRecording && /* @__PURE__ */ jsxs("div", { style: { position: "relative" }, title: recording ? "Stop recording patches" : "Start recording patches", children: [
        /* @__PURE__ */ jsx(
          Button,
          {
            onClick: onToggleRecording,
            variant: "icon",
            iconName: "status-stopped",
            ariaLabel: recording ? "Stop recording" : "Start recording"
          }
        ),
        recording && /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "#d13212",
              pointerEvents: "none"
            }
          }
        )
      ] }),
      onToggleTail && /* @__PURE__ */ jsx("div", { title: tail ? "Keep all patches" : "Keep only last 100 patches", children: /* @__PURE__ */ jsx(
        Button,
        {
          onClick: onToggleTail,
          variant: "icon",
          iconName: tail ? "view-full" : "view-vertical",
          ariaLabel: tail ? "Keep all patches" : "Keep only last 100 patches"
        }
      ) }),
      onToggleExpandAll && /* @__PURE__ */ jsx("div", { title: expandAll ? "Collapse all items" : "Expand all items", children: /* @__PURE__ */ jsx(
        Button,
        {
          onClick: onToggleExpandAll,
          variant: "icon",
          iconName: expandAll ? "caret-down-filled" : "caret-right-filled",
          ariaLabel: expandAll ? "Collapse all" : "Expand all"
        }
      ) }),
      onExport && /* @__PURE__ */ jsx("div", { title: "Export patches as JSON", children: /* @__PURE__ */ jsx(Button, { onClick: onExport, variant: "icon", iconName: "download", ariaLabel: t("exportPatches") }) }),
      onClear && /* @__PURE__ */ jsx("div", { title: "Clear all patches", children: /* @__PURE__ */ jsx(Button, { onClick: onClear, variant: "icon", iconName: "remove", ariaLabel: t("clearPatches") }) }),
      /* @__PURE__ */ jsx("div", { ref: wrapperRef, style: { flex: 1 }, children: /* @__PURE__ */ jsx(
        Autosuggest,
        {
          value: inputValue,
          onChange: ({ detail }) => setInputValue(detail.value),
          options: suggestions,
          onSelect: ({ detail }) => {
            addToken(detail.value);
          },
          placeholder: searchTokens.length === 0 ? placeholder : "Add another search term...",
          ariaLabel: "Search",
          empty: "No matching keys"
        }
      ) })
    ] }),
    searchTokens.length > 0 && /* @__PURE__ */ jsx(
      TokenGroup,
      {
        items: searchTokens.map((token) => ({
          label: token,
          dismissLabel: `Remove ${token}`
        })),
        onDismiss: ({ detail }) => removeToken(detail.itemIndex)
      }
    )
  ] });
};

// src/DebugPanel/filterUtils.ts
function extractAllKeys(obj) {
  const keys = /* @__PURE__ */ new Set();
  function traverse(value) {
    if (value === null || value === void 0) {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => traverse(item));
    } else if (typeof value === "object") {
      Object.keys(value).forEach((key) => {
        keys.add(key);
        traverse(value[key]);
      });
    }
  }
  if (Array.isArray(obj)) {
    obj.forEach((item) => traverse(item));
  } else {
    traverse(obj);
  }
  return Array.from(keys).sort();
}
function objectContains(obj, query) {
  if (obj === null || obj === void 0) {
    return false;
  }
  if (typeof obj !== "object") {
    return String(obj).toLowerCase().includes(query);
  }
  if (Array.isArray(obj)) {
    return obj.some((item) => objectContains(item, query));
  }
  for (const [key, value] of Object.entries(obj)) {
    if (key.toLowerCase().includes(query) || objectContains(value, query)) {
      return true;
    }
  }
  return false;
}
function filterObjectWithTokens(obj, tokens) {
  if (tokens.length === 0) {
    return obj;
  }
  const containsAnyToken = tokens.some((token) => objectContains(obj, token.toLowerCase()));
  if (!containsAnyToken) {
    return null;
  }
  const filtered = {};
  let hasMatch = false;
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }
  if (Array.isArray(obj)) {
    const filteredArray = obj.map((item) => {
      const itemMatchesAny = tokens.some((token) => objectContains(item, token.toLowerCase()));
      if (!itemMatchesAny) {
        return null;
      }
      if (typeof item === "object" && item !== null && !Array.isArray(item)) {
        const itemFiltered = {};
        let itemHasMatch = false;
        for (const [key, value] of Object.entries(item)) {
          const keyMatchesAny = tokens.some((token) => key.toLowerCase().includes(token));
          if (keyMatchesAny) {
            itemFiltered[key] = value;
            itemHasMatch = true;
          }
        }
        return itemHasMatch ? itemFiltered : null;
      }
      return item;
    }).filter((item) => item !== null);
    return filteredArray.length > 0 ? filteredArray : null;
  }
  for (const [key, value] of Object.entries(obj)) {
    const keyMatchesAny = tokens.some((token) => {
      return key.toLowerCase().includes(token.toLowerCase());
    });
    if (keyMatchesAny) {
      filtered[key] = value;
      hasMatch = true;
    } else if (Array.isArray(value)) {
      const filteredArrayValue = value.map((item) => {
        if (typeof item === "object" && item !== null && !Array.isArray(item)) {
          const itemFiltered = {};
          let itemHasMatch = false;
          for (const [itemKey, itemValue] of Object.entries(item)) {
            const itemKeyMatches = tokens.some((token) => itemKey.toLowerCase().includes(token));
            if (itemKeyMatches) {
              itemFiltered[itemKey] = itemValue;
              itemHasMatch = true;
            }
          }
          return itemHasMatch ? itemFiltered : null;
        }
        return null;
      }).filter((item) => item !== null);
      if (filteredArrayValue.length > 0) {
        filtered[key] = filteredArrayValue;
        hasMatch = true;
      }
    }
  }
  return hasMatch ? filtered : null;
}

// src/DebugPanel/StateTab.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var StateTab = ({ state }) => {
  const { t } = useAppTranslation2();
  const [searchTokens, setSearchTokens] = useState2([]);
  const [isDarkMode, setIsDarkMode] = useState2(false);
  const [expandAll, setExpandAll] = useState2(false);
  React2.useEffect(() => {
    const detectTheme = () => {
      const bgColor = getComputedStyle(document.documentElement).getPropertyValue("--color-background-container-content");
      setIsDarkMode(bgColor.includes("#0") || bgColor.includes("#1") || bgColor.includes("#2"));
    };
    detectTheme();
    const observer = new MutationObserver(detectTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-awsui-theme", "class"]
    });
    return () => observer.disconnect();
  }, []);
  const availableKeys = useMemo(() => state ? extractAllKeys(state) : [], [state]);
  const filteredState = useMemo(() => {
    if (!state || searchTokens.length === 0) {
      return state;
    }
    const result = filterObjectWithTokens(state, searchTokens);
    return result;
  }, [state, searchTokens]);
  const handleExport = () => {
    const dataStr = JSON.stringify(state, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `debug-state-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };
  if (!state) {
    return /* @__PURE__ */ jsx2(Box, { padding: "l", textAlign: "center", color: "text-status-inactive", children: "No project state available" });
  }
  const expandLevel = expandAll ? 99 : searchTokens.length > 0 ? 10 : 2;
  return /* @__PURE__ */ jsxs2(SpaceBetween2, { size: "m", children: [
    /* @__PURE__ */ jsx2(
      SearchBar,
      {
        searchTokens,
        onTokensChange: setSearchTokens,
        availableKeys,
        placeholder: t("searchState"),
        onExport: handleExport,
        expandAll,
        onToggleExpandAll: () => setExpandAll(!expandAll)
      }
    ),
    searchTokens.length > 0 && !filteredState ? /* @__PURE__ */ jsx2(Box, { padding: "l", textAlign: "center", color: "text-status-inactive", children: t("noResultsFound") }) : /* @__PURE__ */ jsx2(ObjectInspector, { data: filteredState, expandLevel, theme: isDarkMode ? "chromeDark" : "chromeLight" })
  ] });
};

// src/DebugPanel/PatchesTab.tsx
import React3, { useState as useState3, useMemo as useMemo2 } from "react";
import Box2 from "@cloudscape-design/components/box";
import SpaceBetween3 from "@cloudscape-design/components/space-between";
import { ObjectInspector as ObjectInspector2 } from "react-inspector";
import { useAppTranslation as useAppTranslation3 } from "@amzn/awsmp-max-translations";
import { Fragment, jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var PatchesTab = ({ patches, recording, tail, onExport, onClear, onToggleRecording, onToggleTail }) => {
  const { t } = useAppTranslation3();
  const [searchTokens, setSearchTokens] = useState3([]);
  const [isDarkMode, setIsDarkMode] = useState3(false);
  React3.useEffect(() => {
    const detectTheme = () => {
      const bgColor = getComputedStyle(document.documentElement).getPropertyValue("--color-background-container-content");
      setIsDarkMode(bgColor.includes("#0") || bgColor.includes("#1") || bgColor.includes("#2"));
    };
    detectTheme();
    const observer = new MutationObserver(detectTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-awsui-theme", "class"]
    });
    return () => observer.disconnect();
  }, []);
  const availableKeys = useMemo2(() => extractAllKeys(patches), [patches]);
  const filteredPatches = useMemo2(() => {
    if (searchTokens.length === 0) {
      return patches;
    }
    return patches.map((patch) => filterObjectWithTokens(patch, searchTokens)).filter((patch) => patch !== null);
  }, [patches, searchTokens]);
  return /* @__PURE__ */ jsxs3(SpaceBetween3, { size: "m", children: [
    /* @__PURE__ */ jsx3(
      SearchBar,
      {
        searchTokens,
        onTokensChange: setSearchTokens,
        availableKeys,
        placeholder: t("searchPatches"),
        recording,
        tail,
        onToggleRecording,
        onToggleTail,
        onExport,
        onClear
      }
    ),
    /* @__PURE__ */ jsx3("div", { children: patches.length === 0 ? /* @__PURE__ */ jsx3(Box2, { padding: "l", textAlign: "center", color: "text-status-inactive", children: t("noPatchesRecorded") }) : filteredPatches.length === 0 ? /* @__PURE__ */ jsx3(Box2, { padding: "l", textAlign: "center", color: "text-status-inactive", children: t("noResultsFound") }) : /* @__PURE__ */ jsxs3(Fragment, { children: [
      /* @__PURE__ */ jsxs3(Box2, { variant: "awsui-key-label", children: [
        t("showingPatchesCount", { filtered: filteredPatches.length, total: patches.length }),
        searchTokens.length > 0 && ` ${t("filteredByTokens", {
          count: searchTokens.length,
          plural: searchTokens.length === 1 ? "" : "s"
        })}`
      ] }),
      /* @__PURE__ */ jsx3(
        ObjectInspector2,
        {
          data: filteredPatches,
          expandLevel: searchTokens.length > 0 ? 10 : 1,
          theme: isDarkMode ? "chromeDark" : "chromeLight"
        }
      )
    ] }) })
  ] });
};

// src/DebugPanel/SettingsTab.tsx
import SpaceBetween4 from "@cloudscape-design/components/space-between";
import Box3 from "@cloudscape-design/components/box";
import Toggle from "@cloudscape-design/components/toggle";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var SettingsTab = ({ settings, onToggle }) => {
  return /* @__PURE__ */ jsxs4(SpaceBetween4, { size: "m", children: [
    /* @__PURE__ */ jsx4(Box3, { variant: "h3", children: "Debug Features" }),
    /* @__PURE__ */ jsx4(Toggle, { checked: settings.messages, onChange: () => onToggle("messages"), children: "Debug Messages" }),
    /* @__PURE__ */ jsx4(Toggle, { checked: settings.darkMode, onChange: () => onToggle("darkMode"), children: /* @__PURE__ */ jsxs4(SpaceBetween4, { size: "xxs", direction: "vertical", children: [
      /* @__PURE__ */ jsx4(Box3, { children: "Dark Mode" }),
      /* @__PURE__ */ jsx4(Box3, { variant: "small", color: "text-body-secondary", children: "When enabled, respects system dark mode preference" })
    ] }) }),
    /* @__PURE__ */ jsx4(Toggle, { checked: settings.limitPatches, onChange: () => onToggle("limitPatches"), children: /* @__PURE__ */ jsxs4(SpaceBetween4, { size: "xxs", direction: "vertical", children: [
      /* @__PURE__ */ jsx4(Box3, { children: "Limit Patches" }),
      /* @__PURE__ */ jsx4(Box3, { variant: "small", color: "text-body-secondary", children: "Keep only the latest 100 patches to prevent memory issues" })
    ] }) }),
    /* @__PURE__ */ jsx4(Toggle, { checked: settings.epoHelpPanels, onChange: () => onToggle("epoHelpPanels"), children: /* @__PURE__ */ jsxs4(SpaceBetween4, { size: "xxs", direction: "vertical", children: [
      /* @__PURE__ */ jsx4(Box3, { children: "EPO Help Panels" }),
      /* @__PURE__ */ jsx4(Box3, { variant: "small", color: "text-body-secondary", children: "Show contextual help panel info links on EPO GetOffer page" })
    ] }) }),
    /* @__PURE__ */ jsx4(Toggle, { checked: settings.epoFpsEnabledSeller, onChange: () => onToggle("epoFpsEnabledSeller"), children: "EPO FPS \u2014 seller-side controls" }),
    /* @__PURE__ */ jsx4(Toggle, { checked: settings.epoFpsEnabledBuyer, onChange: () => onToggle("epoFpsEnabledBuyer"), children: "EPO FPS \u2014 buyer-side controls" })
  ] });
};

// src/DebugPanel/DebugPanel.module.scss
var DebugPanel_module_default = {
  panel: "DebugPanel_module_panel",
  header: "DebugPanel_module_header",
  title: "DebugPanel_module_title",
  projectId: "DebugPanel_module_projectId",
  closeButton: "DebugPanel_module_closeButton",
  content: "DebugPanel_module_content"
};

// src/DebugPanel/DebugPanel.tsx
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var DebugPanel = (props) => {
  const { t } = useAppTranslation4();
  const [activeTabId, setActiveTabId] = useState4("state");
  const [position, setPosition] = useState4({ x: 100, y: 100 });
  const [size, setSize] = useState4({ width: 600, height: 500 });
  React4.useEffect(() => {
    const x = Math.max(50, (window.innerWidth - 600) / 2);
    const y = Math.max(50, (window.innerHeight - 500) / 2);
    setPosition({ x, y });
  }, []);
  const tabs = [
    {
      id: "state",
      label: t("state"),
      content: /* @__PURE__ */ jsx5(StateTab, { state: props.currentState })
    },
    {
      id: "patches",
      label: t("patchesWithCount", { count: props.patches.length }),
      content: /* @__PURE__ */ jsx5(
        PatchesTab,
        {
          patches: props.patches,
          recording: props.recording,
          tail: props.tail,
          onExport: props.exportPatches,
          onClear: props.clearPatches,
          onToggleRecording: props.toggleRecording,
          onToggleTail: props.toggleTail
        }
      )
    },
    {
      id: "settings",
      label: t("settings"),
      content: /* @__PURE__ */ jsx5(SettingsTab, { settings: props.settings, onToggle: props.toggleSetting })
    }
  ];
  return /* @__PURE__ */ jsx5(
    Rnd,
    {
      size,
      position,
      onDragStop: (_e, d) => setPosition({ x: d.x, y: d.y }),
      onResizeStop: (_e, _direction, ref, _delta, position2) => {
        setSize({
          width: ref.offsetWidth,
          height: ref.offsetHeight
        });
        setPosition(position2);
      },
      minWidth: 400,
      minHeight: 300,
      maxWidth: "90vw",
      maxHeight: "90vh",
      dragHandleClassName: DebugPanel_module_default.header,
      cancel: `.${DebugPanel_module_default.content}`,
      style: { zIndex: 99999, position: "fixed" },
      enableResizing: {
        top: false,
        right: true,
        bottom: true,
        left: true,
        topRight: false,
        bottomRight: true,
        bottomLeft: true,
        topLeft: false
      },
      children: /* @__PURE__ */ jsxs5("div", { className: DebugPanel_module_default.panel, children: [
        /* @__PURE__ */ jsxs5("div", { className: DebugPanel_module_default.header, children: [
          /* @__PURE__ */ jsxs5("div", { className: DebugPanel_module_default.title, children: [
            "\u{1F41B} Debug Panel",
            props.trackedProjectId && /* @__PURE__ */ jsx5("span", { className: DebugPanel_module_default.projectId, children: props.trackedProjectId })
          ] }),
          /* @__PURE__ */ jsx5("button", { className: DebugPanel_module_default.closeButton, onClick: props.togglePanel, "aria-label": t("closeDebugPanel"), children: "\xD7" })
        ] }),
        /* @__PURE__ */ jsx5("div", { className: DebugPanel_module_default.content, children: /* @__PURE__ */ jsx5(Tabs, { activeTabId, onChange: ({ detail }) => setActiveTabId(detail.activeTabId), tabs }) })
      ] })
    }
  );
};

// src/DebugPanel/DebugPanelContainer.module.scss
var DebugPanelContainer_module_default = {
  debugButton: "DebugPanelContainer_module_debugButton",
  badge: "DebugPanelContainer_module_badge"
};

// src/DebugPanel/DebugPanelContainer.tsx
import { Fragment as Fragment2, jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
var DebugPanelContainer = (props) => {
  const { enabled, isOpen, patchCount, togglePanel } = props;
  const handleClick = React5.useCallback(() => {
    togglePanel();
  }, [togglePanel]);
  if (!enabled) {
    return null;
  }
  return /* @__PURE__ */ jsxs6(Fragment2, { children: [
    /* @__PURE__ */ jsxs6("button", { className: DebugPanelContainer_module_default.debugButton, "data-testid": "debug-panel-button", onClick: handleClick, "aria-label": "Toggle debug panel", children: [
      "\u{1F41B} Debug",
      patchCount > 0 && /* @__PURE__ */ jsx6("span", { className: DebugPanelContainer_module_default.badge, children: patchCount })
    ] }),
    isOpen && /* @__PURE__ */ jsx6(DebugPanel, { ...props })
  ] });
};
export {
  DebugPanel,
  DebugPanelContainer,
  PatchesTab,
  SettingsTab,
  StateTab
};
//# sourceMappingURL=index.js.map
